// Libraries
import React from "react";

import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { createStore, combineReducers, applyMiddleware, compose } from "redux";
import thunk from "redux-thunk";
import { useRoutes, navigate } from "raviger";
import { fetchAuthRoles } from "./lib/FetchAuthRoles";
import { AuthProvider } from "oidc-react";

import NoPageFound from "./pages/Shared/NoPageFound";

// Reducers
import adminGroupReducer from "./store/reducers/adminGroup";
import communicationGroupReducer from "./store/reducers/communicationGroups";
import announcementReducer from "./store/reducers/announcement";
import authenticationReducer from "./store/reducers/authentication";
import alertReducer from "./store/reducers/alert";
import branchOfficeMlsProviderReducer from "./store/reducers/branchOfficeMlsProvider";
import breadcrumbReducer from "./store/reducers/breadcrumb";
import commissionPlanReducer from "./store/reducers/commissionPlan";
import commissionPlanTemplateReducer from "./store/reducers/commissionPlanTemplate";
import commissionPlanTemplateTypeReducer from "./store/reducers/commissionPlanTemplateType";
import commissionPlanChangeWaitingPeriodReducer from "./store/reducers/commissionPlanChangeWaitingPeriod";
import commissionPlanFeeTemplateReducer from "./store/reducers/commissionPlanFeeTemplate";
import commissionPlanDocumentsReducer from "./store/reducers/commissionPlanDocuments";
import doingBusinessAsReducer from "./store/reducers/doingBusinessAs";
import emailReducer from "./store/reducers/email";
import errorReducer from "./store/reducers/error";
import feeReducer from "./store/reducers/fee";
import fileReducer from "./store/reducers/file";
import formReducer from "./store/reducers/form";
import franchiseAccountingReducer from "./store/reducers/franchiseAccounting";
import franchiseAdminReducer from "./store/reducers/franchiseAdmin";
import franchiseAgentProductReducer from "./store/reducers/franchiseAgentProduct";
import franchiseAgentProductFeeReducer from "./store/reducers/franchiseAgentProductFee";
import franchiseIncomeAccountsReducer from "./store/reducers/franchiseIncomeAccounts";
import franchiseMlsProviderReducer from "./store/reducers/franchiseMlsProvider";
import franchiseProductReducer from "./store/reducers/franchiseProduct";
import franchiseProductFeeReducer from "./store/reducers/franchiseProductFee";
import franchiseProductTemplateReducer from "./store/reducers/FranchiseProductTemplate";
import franchiseProductTemplateFeeReducer from "./store/reducers/FranchiseProductTemplateFee";
import franchiseReducer from "./store/reducers/franchise";
import franchiseRoyaltyPlanReducer from "./store/reducers/franchiseRoyaltyPlan";
import franchiseRoyaltyPlanFeeReducer from "./store/reducers/franchiseRoyaltyPlanFee";
import franchiseUserReducer from "./store/reducers/franchiseUser";
import globalFileReducer from "./store/reducers/globalFile";
import lookupTypeReducer from "./store/reducers/lookupType";
import lookupCategoryReducer from "./store/reducers/lookupCategory";
import marketingAreaReducer from "./store/reducers/marketingArea";
import marketplaceAssetReducer from "./store/reducers/marketplaceAsset";
import marketplaceVendorReducer from "./store/reducers/marketplaceVendor";
import marketplaceCategoryReducer from "./store/reducers/marketplaceCategory";
import marketplaceDealReducer from "./store/reducers/marketplaceDeal";
import marketplaceFranchiseReducer from "./store/reducers/marketplaceFranchise";
import masterBlasterReducer from "./store/reducers/masterBlaster";
import mlsProviderFranchiseReducer from "./store/reducers/mlsProviderFranchise";
import mlsProviderReducer from "./store/reducers/mlsProvider";
import netSuiteItemsReducer from "./store/reducers/netSuiteItems";
import noteReducer from "./store/reducers/note";
import officeReducer from "./store/reducers/office";
import phoneNumberReducer from "./store/reducers/phoneNumber";
import productReducer from "./store/reducers/product";
import realEstateBoardReducer from "./store/reducers/realEstateBoard";
import departmentOfRealEstateReducer from "./store/reducers/departmentOfRealEstate";
import reportReducer from "./store/reducers/report";
import royaltyPlanTemplateReducer from "./store/reducers/royaltyPlanTemplate";
import royaltyPlanFeeTemplateReducer from "./store/reducers/royaltyPlanFeeTemplate";
import territoryReducer from "./store/reducers/territory";
import userReducer from "./store/reducers/user";
import utilityReducer from "./store/reducers/utility";
import selectedAttachmentReducer from "./store/reducers/selectedAttachment";

// Global styles
import "./index.css";
import "font-awesome/css/font-awesome.min.css";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import "react-bootstrap-table2-filter/dist/react-bootstrap-table2-filter.min.css";
import "@syncfusion/ej2-base/styles/bootstrap.css";
import "@syncfusion/ej2-inputs/styles/bootstrap.css";
import "@syncfusion/ej2-react-dropdowns/styles/bootstrap.css";
import "@syncfusion/ej2-buttons/styles/bootstrap.css";

// React 16.9 hooks+context (slow replacement for Redux)
import createStoreProvider from "./store/createStoreProvider";
import { ActivityPlansProvider } from "./store/providers/ActivityPlans";
import { ActivityTemplatesProvider } from "./store/providers/ActivityTemplates";
import { AgentListReportProvider } from "./store/providers/AgentListReport";
import { AlertProvider } from "./store/providers/Alert";
import { AuthenticationProvider } from "./store/providers/Authentication";
import { AuthenticationMlsProvider } from "./store/providers/AuthenticationMls";
import { BranchOfficeProvider } from "./store/providers/BranchOffice";
import { BreadcrumbProvider } from "./store/providers/Breadcrumb";
import { BrokerMetricsProvider } from "./store/providers/BrokerMetrics";
import { CenteralizedServiceProvider } from "./store/providers/CentralizedService";
import { ClassProvider } from "./store/providers/Class";
import { ContractProvider } from "./store/providers/Contract";
import { ChartsProvider } from "./store/providers/Charts";
import { CommissionPlanProvider } from "./store/providers/CommissionPlan";
import { CommissionPlanFeeTemplateProvider } from "./store/providers/CommissionPlanFeeTemplate";
import { DbaProvider } from "./store/providers/Dba";
import { FaqsProvider } from "./store/providers/Faqs";
import { FaqCategoriesProvider } from "./store/providers/FaqCategories";
import { DownloadInstancesProvider } from "./store/providers/DownloadInstances";
import { FeatureProvider } from "./store/providers/Feature";
import { FranchiseAccountingProvider } from "./store/providers/FranchiseAccounting";
import { FranchiseAgentProductsProvider } from "./store/providers/FranchiseAgentProducts";
import { FranchiseProductsProvider } from "./store/providers/FranchiseProducts";
import { FranchiseDisclaimersProvider } from "./store/providers/FranchiseDisclaimers";
import { FranchiseFineProvider } from "./store/providers/FranchiseFine";
import { FranchiseIncomeAccountsProvider } from "./store/providers/FranchiseIncomeAccounts";
import { FranchiseProvider } from "./store/providers/Franchise";
import { FranchiseEntityProvider } from "./store/providers/FranchiseEntity";
import { FranchiseFilesProvider } from "./store/providers/FranchiseFiles";
import { FranchiseMlsProviderProvider } from "./store/providers/FranchiseMlsProviders";
import { GlobalFilesProvider } from "./store/providers/GlobalFiles";
import { GlobalSettingsProvider } from "./store/providers/GlobalSettings";
import { TransactionDetailsProvider } from "./store/providers/TransactionDetails";
import { LedgerProvider } from "./store/providers/Ledger";
import { LookupTypesProvider } from "./store/providers/LookupType";
import { MailMergeFieldProvider } from "./store/providers/MailMergeField";
import { MailMergeCategoryProvider } from "./store/providers/MailMergeCategory";
import { MailMergeTemplateProvider } from "./store/providers/MailMergeTemplate";
import { MarketplaceAssetProvider } from "./store/providers/MarketplaceAsset";
import { MarketplaceCategoryProvider } from "./store/providers/MarketplaceCategory";
import { MarketplaceDealProvider } from "./store/providers/MarketplaceDeal";
import { MarketplaceFranchiseProvider } from "./store/providers/MarketplaceFranchise";
import { MarketplaceVendorProvider } from "./store/providers/MarketplaceVendor";
import { MlsProvidersProvider } from "./store/providers/MlsProviders";
import { MlsProviderFileProvider } from "./store/providers/MlsProviderFile";
import { MlsProviderFranchiseProvider } from "./store/providers/MlsProviderFranchise";
import { MlsProviderStatusProvider } from "./store/providers/MlsProviderStatus";
import { MlsMetricsProvider } from "./store/providers/MlsMetrics";
import { NetSuiteItemsProvider } from "./store/providers/NetSuiteItems";
import { NetSuiteLocationProvider } from "./store/providers/NetSuiteLocation";
import { NetSuiteMarketAreaProvider } from "./store/providers/NetSuiteMarketArea";
import { NetSuiteAllItemsProvider } from "./store/providers/NetSuiteAllItems";
import { NewsProvider } from "./store/providers/News";
import { OfficeTypeProvider } from "./store/providers/OfficeType";
import { OfficeNetsuiteClassProvider } from "./store/providers/OfficeNetsuiteClass";
import { PaymentSubscriptionCategoryProvider } from "./store/providers/PaymentSubscriptionCategory";
import { ProductsProvider } from "./store/providers/Products";
import { PromotionProvider } from "./store/providers/Promotion";
import { RealEstateBoardProvider } from "./store/providers/RealEstateBoard";
import { DepartmentOfRealEstateProvider } from "./store/providers/DepartmentOfRealEstate";
import { ReportProvider } from "./store/providers/Report";
import { ReportApplicationProvider } from "./store/providers/ReportApplication";
import { ReportCategoryProvider } from "./store/providers/ReportCategory";
import { ReportFranchiseProvider } from "./store/providers/ReportFranchise";
import { RoleProvider } from "./store/providers/Role";
import { RoyaltyPlanTemplateProvider } from "./store/providers/RoyaltyPlanTemplate";
import { RSASettingsTabProvider } from "./store/providers/RSASettingsTab";
import { GmailGroupsProvider } from "./store/providers/GmailGroups";
import { MembersProvider } from "./store/providers/Members";

import { StateProvider } from "./store/providers/State";
import { StatusProvider } from "./store/providers/Status";
import { SurveyTemplateCategoryProvider } from "./store/providers/SurveyTemplateCategories";
import { SurveyTemplatesProvider } from "./store/providers/SurveyTemplates";
import { TerritoryProvider } from "./store/providers/Territory";
import { TimeZoneProvider } from "./store/providers/TimeZone";
import { UserManagementProvider } from "./store/providers/UserManagement";
import { FranchiseRolesProvider } from "./store/providers/FranchiseRoles";
import { TransactionDocumentProvider } from "./store/providers/TransactionDocument";
import { TransactionDocumentLookupsProvider } from "./store/providers/TransactionDocumentLookups";
import { RSAMenuSettingsProvider } from "./store/providers/RSAMenuSettings";
import { TransactionDocumentSourceProvider } from "./store/providers/TransactionDocumentSource";
import { TransactionDocumentPagesProvider } from "./store/providers/TransactionDocumentPages";
import { TransactionFeeProvider } from "./store/providers/TransactionFee";
import { TransactionContractTypeProvider } from "./store/providers/TransactionContractType";
import { TransactionTypeProvider } from "./store/providers/TransactionType";
import { PercentageOfProvider } from "./store/providers/PercentageOf";
import { AgentFeeProvider } from "./store/providers/AgentFee";
import { FranchiseProductsTemplatesProvider } from "./store/providers/FranchiseProductsTemplates";
import { CommissionPlanTemplateWaitingPeriodsProvider } from "./store/providers/CommissionPlanTemplateWaitingPeriods";
import { ReportCustomEmailProvider } from "./store/providers/ReportCustomEmail";
import { VideoCoursesProvider } from "./store/providers/VideoCourses";
import { ZipcodeProvider } from "./store/providers/Zipcode";
import { CommunicationGroupsProvider } from "./store/providers/CommunicationGroup";

import moment from "moment";
import Routes from "./routes";
import * as constants from "./constants";
import { SmartDocCopyProvider } from "./store/providers/SmartDoc";
import { FranchiseScoreCardProvider } from "./store/providers/FranchiseScoreCard";

// Set root reducer
const rootReducer = combineReducers({
  adminGroup: adminGroupReducer,
  communicationGroup: communicationGroupReducer,
  announcement: announcementReducer,
  authentication: authenticationReducer,
  alert: alertReducer,
  branchOfficeMlsProvider: branchOfficeMlsProviderReducer,
  breadcrumb: breadcrumbReducer,
  commissionPlan: commissionPlanReducer,
  commissionPlanTemplate: commissionPlanTemplateReducer,
  commissionPlanTemplateType: commissionPlanTemplateTypeReducer,
  commissionPlanChangeWaitingPeriod: commissionPlanChangeWaitingPeriodReducer,
  commissionPlanFeeTemplate: commissionPlanFeeTemplateReducer,
  commissionPlanDocuments: commissionPlanDocumentsReducer,
  doingBusinessAs: doingBusinessAsReducer,
  email: emailReducer,
  error: errorReducer,
  fee: feeReducer,
  file: fileReducer,
  form: formReducer,
  franchise: franchiseReducer,
  franchiseAccounting: franchiseAccountingReducer,
  franchiseAdmin: franchiseAdminReducer, // Depercated.
  franchiseAgentProduct: franchiseAgentProductReducer,
  franchiseAgentProductFee: franchiseAgentProductFeeReducer,
  franchiseIncomeAccounts: franchiseIncomeAccountsReducer,
  franchiseMlsProvider: franchiseMlsProviderReducer,
  franchiseProduct: franchiseProductReducer,
  franchiseProductFee: franchiseProductFeeReducer,
  franchiseProductTemplate: franchiseProductTemplateReducer,
  franchiseProductTemplateFee: franchiseProductTemplateFeeReducer,
  franchiseRoyaltyPlan: franchiseRoyaltyPlanReducer, // Deprecated.
  franchiseRoyaltyPlanFee: franchiseRoyaltyPlanFeeReducer, // Depercated.
  franchiseUser: franchiseUserReducer,
  globalFile: globalFileReducer,
  lookupType: lookupTypeReducer,
  lookupCategory: lookupCategoryReducer,
  marketingArea: marketingAreaReducer,
  marketplaceAsset: marketplaceAssetReducer,
  marketplaceDeal: marketplaceDealReducer,
  marketplaceVendor: marketplaceVendorReducer,
  marketplaceCategory: marketplaceCategoryReducer,
  marketplaceFranchise: marketplaceFranchiseReducer,
  masterBlaster: masterBlasterReducer,
  mlsProvider: mlsProviderReducer,
  mlsProviderFranchise: mlsProviderFranchiseReducer,
  netSuiteItems: netSuiteItemsReducer,
  note: noteReducer,
  office: officeReducer,
  phoneNumber: phoneNumberReducer,
  product: productReducer,
  realEstateBoard: realEstateBoardReducer,
  departmentOfRealEstate: departmentOfRealEstateReducer,
  royaltyPlanTemplate: royaltyPlanTemplateReducer,
  royaltyPlanFeeTemplate: royaltyPlanFeeTemplateReducer,
  report: reportReducer,
  selectedAttachment: selectedAttachmentReducer,
  territory: territoryReducer,
  user: userReducer,
  utility: utilityReducer,
});

// React 16.9 context+hooks (replacement for Redux)
const StoreProvider = createStoreProvider([
  AuthenticationProvider, // NOTE: Must remain above all others
  AuthenticationMlsProvider, // NOTE: Must remain above all others
  AlertProvider, // NOTE: Must remain above all others except AuthenticationProvider
  ActivityPlansProvider,
  ActivityTemplatesProvider,
  AgentListReportProvider,
  BranchOfficeProvider,
  BreadcrumbProvider,
  BrokerMetricsProvider,
  CenteralizedServiceProvider,
  ClassProvider,
  ContractProvider,
  ChartsProvider,
  CommissionPlanProvider,
  CommissionPlanFeeTemplateProvider,
  DbaProvider,
  DownloadInstancesProvider,
  FaqsProvider,
  FaqCategoriesProvider,
  FeatureProvider,
  FranchiseFilesProvider,
  FranchiseFineProvider,
  FranchiseProvider,
  FranchiseEntityProvider,
  FranchiseDisclaimersProvider,
  FranchiseIncomeAccountsProvider,
  FranchiseAccountingProvider,
  FranchiseAgentProductsProvider,
  FranchiseProductsProvider,
  FranchiseMlsProviderProvider,
  GmailGroupsProvider,
  MembersProvider,
  FranchiseRolesProvider,
  GlobalFilesProvider,
  GlobalSettingsProvider,
  LookupTypesProvider,
  LedgerProvider,
  MailMergeFieldProvider,
  MailMergeCategoryProvider,
  MailMergeTemplateProvider,
  MarketplaceAssetProvider,
  MarketplaceCategoryProvider,
  MarketplaceDealProvider,
  MarketplaceFranchiseProvider,
  MarketplaceVendorProvider,
  MlsProvidersProvider,
  MlsProviderFileProvider,
  MlsProviderFranchiseProvider,
  MlsProviderStatusProvider,
  MlsMetricsProvider,
  NetSuiteAllItemsProvider,
  NetSuiteItemsProvider,
  NetSuiteLocationProvider,
  NetSuiteMarketAreaProvider,
  NewsProvider,
  OfficeTypeProvider,
  OfficeNetsuiteClassProvider,
  PaymentSubscriptionCategoryProvider,
  ProductsProvider,
  PromotionProvider,
  RealEstateBoardProvider,
  DepartmentOfRealEstateProvider,
  ReportProvider,
  ReportApplicationProvider,
  ReportCategoryProvider,
  ReportFranchiseProvider,
  RoyaltyPlanTemplateProvider,
  RSASettingsTabProvider,
  GmailGroupsProvider,
  RoleProvider,
  StateProvider,
  StatusProvider,
  SurveyTemplateCategoryProvider,
  SurveyTemplatesProvider,
  TimeZoneProvider,
  UserManagementProvider,
  TerritoryProvider,
  TransactionDetailsProvider,
  TransactionDocumentProvider,
  TransactionDocumentLookupsProvider,
  RSAMenuSettingsProvider,
  TransactionDocumentSourceProvider,
  TransactionDocumentPagesProvider,
  TransactionFeeProvider,
  TransactionContractTypeProvider,
  TransactionTypeProvider,
  PercentageOfProvider,
  AgentFeeProvider,
  FranchiseProductsTemplatesProvider,
  CommissionPlanTemplateWaitingPeriodsProvider,
  ReportCustomEmailProvider,
  VideoCoursesProvider,
  ZipcodeProvider,
  CommunicationGroupsProvider,
  SmartDocCopyProvider,
  FranchiseScoreCardProvider,
]);

// Setup the debugging tools (if they exist and create store)
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const store = createStore(
  rootReducer,
  composeEnhancers(applyMiddleware(thunk))
);

function RouteProvider() {
  const routes = useRoutes(Routes);
  return routes || <NoPageFound />;
}

let oidcAuthority = process.env.REACT_APP_OIDC_AUTHORITY;
let oidcRedirectUri = process.env.REACT_APP_PROJECT_URL;

const envConfig = constants.EnvironmentConfig[process.env.REACT_APP_ENV];
if (envConfig) {
  oidcAuthority = envConfig.oidcAuthority || oidcAuthority;
  oidcRedirectUri = envConfig.oidcRedirectUri || oidcRedirectUri;
}
const oidcConfig = {
  authority: oidcAuthority,
  clientId: process.env.REACT_APP_OIDC_CLIENT_ID,
  redirectUri: oidcRedirectUri,
  postLogoutRedirectUri: oidcRedirectUri,
  onSignIn: async (user) => {
    const authData = await fetchAuthRoles(user.profile.sub);

    localStorage.setItem(
      "authData",
      JSON.stringify({
        accessToken: user.access_token,
        refreshToken: user.refresh_token,
        expireDate: moment
          .unix(user.expires_at)
          .format("YYYY-MM-DDTHH:mm:ss.sssZ"),
        fullName: user.profile.name,
        username: user.profile.preferred_username,
        userId: user.profile.sub,
        roles: authData.body.fmRoles,
        permissions: authData.body.fmPermissions,
      })
    );

    navigate("/dashboard");
  },
  onSignOut: async () => {
    localStorage.clear();
  },
};

function App() {
  return (
    <AuthProvider {...oidcConfig}>
      <StoreProvider>
        <Provider store={store}>
          <RouteProvider />
        </Provider>
      </StoreProvider>
    </AuthProvider>
  );
}

ReactDOM.render(App(), document.getElementById("root"));
